import { useMemo } from 'react';

function getCompletionCounts(formData) {
  const textFields = [
    'gapAnalysis',
    'evaluationProcess',
    'integrityMatters',
    'diversityIssues',
    'fnsFeedback',
    'generalObservations',
  ];

  let filled = 0;
  let total = 0;

  textFields.forEach((field) => {
    total += 1;
    if (formData[field]?.trim()) filled += 1;
  });

  formData.memberAttendance.forEach((member) => {
    total += 1;
    if (member.attendance?.trim()) filled += 1;
  });

  formData.candidateReports.forEach((cr) => {
    total += 1;
    if (cr.report?.trim()) filled += 1;
  });

  return { filled, total };
}

const useDeadlineInfo = (
  deadline,
  isSubmitted,
  formData,
  hasWordLimitErrors = false,
) => {
  const isPastDeadline = useMemo(() => {
    if (!deadline) return false;
    return new Date() > new Date(deadline);
  }, [deadline]);

  const deadlineInfo = useMemo(() => {
    if (!deadline) return null;
    if (isPastDeadline) {
      return {
        variant: 'danger',
        icon: 'fa-lock',
        message: `The deadline for this report has passed (${new Date(deadline).toLocaleDateString('en-AU')}).`,
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const deadlineDay = new Date(deadline);
    deadlineDay.setHours(0, 0, 0, 0);

    const daysLeft = Math.round((deadlineDay - today) / (1000 * 60 * 60 * 24));

    const deadlineDate = new Date(deadline);

    let completionSuffix = '';
    if (!isSubmitted) {
      const { filled, total } = getCompletionCounts(formData);
      if (filled === 0) {
        completionSuffix = ' You have not yet started your report.';
      } else if (filled < total && hasWordLimitErrors) {
        completionSuffix = ` ${filled} of ${total} fields completed, and some exceed the word limit.`;
      } else if (filled < total) {
        completionSuffix = ` ${filled} of ${total} fields completed.`;
      } else if (hasWordLimitErrors) {
        completionSuffix =
          ' All fields completed, but some exceed the word limit.';
      } else {
        completionSuffix = ' All fields completed — ready to submit!';
      }
    }

    if (daysLeft <= 2) {
      return {
        variant: 'danger',
        icon: 'fa-exclamation-triangle',
        message: `Report due in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${deadlineDate.toLocaleDateString('en-AU')}).${completionSuffix}`,
      };
    }
    if (daysLeft <= 7) {
      return {
        variant: 'warning',
        icon: 'fa-clock',
        message: `Report due in ${daysLeft} days (${deadlineDate.toLocaleDateString('en-AU')}).${completionSuffix}`,
      };
    }
    return {
      variant: 'info',
      icon: 'fa-clock',
      message: `Report due by ${deadlineDate.toLocaleDateString('en-AU')}.${completionSuffix}`,
    };
  }, [deadline, isPastDeadline, isSubmitted, formData, hasWordLimitErrors]);

  return { isPastDeadline, deadlineInfo };
};

export default useDeadlineInfo;
